import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Send, Paperclip, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const MessageCompose = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const recipientId = searchParams.get('to');
  const proposalId = searchParams.get('proposalId');
  const action = searchParams.get('action'); // 'reject' for rejection messages
  const initialMessage = searchParams.get('message') ? decodeURIComponent(searchParams.get('message')) : '';

  const [recipient, setRecipient] = useState(null);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState(initialMessage);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (recipientId) {
      fetchRecipientInfo();
    }
    
    // Set subject based on action
    if (action === 'reject' && proposalId) {
      setSubject('Regarding Your Training Proposal');
    }
  }, [recipientId, action, proposalId]);

  const fetchRecipientInfo = async () => {
    try {
      const token = localStorage.getItem('token');
      // TODO: Implement get user by ID endpoint
      // For now, we'll use mock data
      setRecipient({
        id: recipientId,
        name: 'Trainer',
        email: 'trainer@example.com',
        avatar: null
      });
    } catch (err) {
      console.error('Error fetching recipient:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    if (!message.trim()) {
      alert('Please enter a message');
      return;
    }

    setSending(true);

    try {
      const token = localStorage.getItem('token');
      
      // If this is a rejection message, also update the proposal status
      if (action === 'reject' && proposalId) {
        await axios.put(
          `${API_BASE_URL}/api/proposals/${proposalId}/status?status=rejected`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      // TODO: Implement message sending API
      console.log('Sending message to:', recipientId, 'Subject:', subject, 'Message:', message);
      
      alert('Message sent successfully!');
      navigate(-1);
    } catch (err) {
      console.error('Error sending message:', err);
      alert('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button variant="ghost" onClick={() => navigate(-1)}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <CardTitle>
                {action === 'reject' ? 'Send Rejection Message' : 'Compose Message'}
              </CardTitle>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Recipient */}
          <div>
            <Label className="text-sm font-medium text-gray-700 mb-2 block">To</Label>
            {recipient && (
              <div className="flex items-center space-x-3 p-3 border rounded-lg bg-gray-50">
                <Avatar className="w-10 h-10">
                  <AvatarImage src={recipient.avatar} alt={recipient.name} />
                  <AvatarFallback>{recipient.name?.charAt(0) || 'T'}</AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-medium">{recipient.name}</p>
                  <p className="text-sm text-gray-600">{recipient.email}</p>
                </div>
              </div>
            )}
          </div>

          {/* Subject */}
          <div>
            <Label htmlFor="subject" className="text-sm font-medium text-gray-700 mb-2 block">
              Subject
            </Label>
            <Input
              id="subject"
              type="text"
              placeholder="Enter subject..."
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full"
            />
          </div>

          {/* Message */}
          <div>
            <Label htmlFor="message" className="text-sm font-medium text-gray-700 mb-2 block">
              Message
            </Label>
            <Textarea
              id="message"
              rows={12}
              placeholder="Type your message here..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="w-full"
            />
            <p className="text-xs text-gray-500 mt-1">
              {message.length} characters
            </p>
          </div>

          {/* Helpful tips for rejection messages */}
          {action === 'reject' && (
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h4 className="font-semibold text-blue-900 mb-2">Tips for Rejection Messages:</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Thank the trainer for their time and effort</li>
                <li>• Be respectful and professional</li>
                <li>• Keep the message concise and clear</li>
                <li>• You don't need to provide detailed reasons</li>
                <li>• Wish them success in future opportunities</li>
              </ul>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-between items-center pt-4 border-t">
            <Button variant="outline" onClick={() => navigate(-1)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSend} 
              disabled={sending || !message.trim()}
              className={`${
                action === 'reject' 
                  ? 'bg-red-600 hover:bg-red-700' 
                  : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {sending ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  {action === 'reject' ? 'Send & Reject Proposal' : 'Send Message'}
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default MessageCompose;
